import React, { useState, createRef, useRef, useEffect } from 'react';
import ParentIcon from '@mui/icons-material/AccountBalanceOutlined';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useOnClickOutside } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import { selectShareFolder, switchShareModal, setClickedFolderName } from '../../../store/folders/foldersSlice';
import { editFolderName, postFolderViewRequest } from '../../../api/foldersApi';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import { SortOptions, SortType } from '../../../enums/SORT';
import { MoreIcon } from '../../../assets/icons/threedots';
import { ReactComponent as UsersIcon } from '../../../assets/icons/CollaboratorsUsersIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { COLLABORATIVE_FOLDER_TYPE } from '../../../enums/TYPES';
import EditButton from '../../modules/EditButton';
import { PATHS } from '../../../enums/PATHS';
import { bytesToGB, bytesToMB, secondsToHours, useDimensions } from '../../../helpers';
import { useOverflowAwareActionMenu } from '../../../helpers/useOverflowAwareActionMenu';
import styles from './CollaborativeFolder.module.scss';
import TrashButton from '../TrashButton';
import ShareButton from '../ShareButton';
import DeleteCollaborativeFolderModal from '../../CollaborativeFolders/DeleteCollaborativeFolderModal';
import { Tooltip } from '@material-ui/core';
import EditCollaboratorsModal from '../../CollaborativeFolders/EditCollaboratorsModal';
import ShareFolderToChildModal from '../../CollaborativeFolders/ShareFolderToChildModal';
import { Portal } from '../../../helpers/bodyPortal';
import { convertSeconds } from '../../Timeline/utils';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../DateSortHeader';

type TCollaborativeFolder = {
  folder: COLLABORATIVE_FOLDER_TYPE;
  data: { page: number; limit: number; search?: string; sort?: SortType; sortOption: SortOptions };
  folderIndex: number;
  fetchDataCallback: () => void;
};

const CollaborativeFolder: React.FC<TCollaborativeFolder> = ({ folder, data, folderIndex, fetchDataCallback }) => {
  const history = useHistory();
  const notification = useSnackbar();
  const redirectRef = createRef<HTMLDivElement>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [folderName, setFolderName] = useState(folder.name);
  const dispatch = useAppDispatch();

  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({ showMenu: showDotsMenu });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
  };

  const handleEdit = async () => {
    if (folderName.trim().length === 0) {
      setFolderName(folder.name); // reset the input to the previous value
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);
    await editFolderName(folderName, folder.id);
    if (fetchDataCallback) {
      fetchDataCallback();
    }
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  const enableFolderNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const onShareClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    dispatch(selectShareFolder(folder));
    dispatch(switchShareModal(true));
  };

  const [showChildOrgModal, setShowChildOrgModal] = useState(false);
  const onShareToChildOrgsClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowChildOrgModal(!showChildOrgModal);
  };

  const redirectToContent = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isEditing) return;
    if (e.currentTarget.contains(e.target as Node)) {
      dispatch(setClickedFolderName(folder.name));
      postFolderViewRequest(e.currentTarget.id);
      history.push(
        `${PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path}?folderId=${e.currentTarget.id}&parentPage=${PATHS.COLLABORATIVE_FOLDERS.label}`,
      );
    }
  };

  const switchDeleteModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteModal(!showDeleteModal);
  };

  const handleFolderIconClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isEditing) return;
    e.stopPropagation();
    dispatch(setClickedFolderName(folder.name));
    postFolderViewRequest(folder.id);
    history.push(`${PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path}?folderId=${folder.id}&parentPage=${PATHS.COLLABORATIVE_FOLDERS.label}`);
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }
    setShowDotsMenu(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const { widthD } = useDimensions();

  const [editCollModal, setEditCollModal] = useState(false);
  const switchEditCollModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setEditCollModal(!editCollModal);
  };

  return (
    <div
      onClick={redirectToContent}
      onContextMenu={switchDotsMenu}
      id={folder.id}
      className={cx(styles.container, showDotsMenu && styles.selectedFolder)}
      style={{ position: 'relative' }}
    >
      <div ref={redirectRef} className={styles.link}>
        <div className={styles.leftSide} id={folder.id} onClick={redirectToContent}>
          <img src={FolderIcon} alt="folder" title="foldericon" style={{ cursor: 'pointer' }} onClick={handleFolderIconClick} />

          <div />
          {isEditing ? (
            <form
              onClick={(e) => {
                e.stopPropagation();
              }}
              onSubmit={handleEdit}
            >
              <input className={styles.input} onChange={handleChange} value={folderName} onBlur={handleEdit} ref={inputRef} />
            </form>
          ) : (
            <div className={styles.name}>
              <span>{folderName}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.rightSide}>
        <div className={styles.team}>
          <Tooltip
            title={
              <div>
                {folder.collaborators.map((u) => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {' '}
                    <UsersIcon style={{ marginRight: '8px' }} />
                    {u?.username}
                  </div>
                ))}
              </div>
            }
          >
            <div className={styles.users}>
              <UsersIcon />
              {folder.collaborators.length}
            </div>
          </Tooltip>

          {folder?.parent_org_name && (
            <Tooltip title={`Parent organisation: ${folder.parent_org_name}`}>
              <ParentIcon style={{ fill: '#6500aa', fontSize: '16px' }} />
            </Tooltip>
          )}
        </div>
        <Tooltip
          title={`${getDateColumnTooltipLabel(data.sortOption)}: ${DateFormatter(getDateColumnTooltipValue(folder, data.sortOption), true)}`}
        >
          <div className={styles.date}>
            {widthD < 1300
              ? DateFormatter(getDateColumnValue(folder, data.sortOption))
              : DateFormatter(getDateColumnValue(folder, data.sortOption), true)}
          </div>
        </Tooltip>
        <div className={styles.size}>
          <img src={StorageIcon} alt="storage" />
          {bytesToGB(Number(folder?.size)) > 1
            ? `${bytesToGB(Number(folder?.size)).toFixed(2)} GB`
            : `${bytesToMB(Number(folder?.size)).toFixed(0)} MB`}
        </div>

        <div className={styles.duration}>
          {folder?.duration && (
            <Tooltip title={convertSeconds(folder.duration, false, true)}>
              <div>{secondsToHours(folder.duration)}</div>
            </Tooltip>
          )}
        </div>

        <div className={styles.dotsMenuBtn} onClick={switchDotsMenu}>
          <div ref={triggerRef} className={styles.dotIcon}>
            <MoreIcon />
          </div>
        </div>
      </div>
      <DeleteCollaborativeFolderModal
        open={showDeleteModal}
        switchModal={() => setShowDeleteModal(!showDeleteModal)}
        folderId={folder.id}
        data={data}
        fetchCallBack={fetchDataCallback}
      />
      <Portal>
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            display: showDotsMenu ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
            right: 'auto',
          }}
        >
          <div className={styles.menuOption} onClick={enableFolderNameEdit}>
            <EditButton /> Rename
          </div>

          {myOrganization.type === 'child' && (
            <div className={styles.menuOption} onClick={onShareClick}>
              <ShareButton /> Share
            </div>
          )}

          {myOrganization.type === 'parent' && (
            <div className={styles.menuOption} onClick={onShareToChildOrgsClick}>
            <ShareButton /> Share with child organisations
            </div>
          )}

          <div className={styles.menuOption} onClick={switchEditCollModal}>
            <UsersIcon className={styles.addCollBtn} /> Add collaborator
          </div>
          {(currentUser?.email === folder?.owner?.email || folder.parent_folder_id) && (
            <div className={styles.menuOption} onClick={switchDeleteModal}>
              <TrashButton /> Delete
            </div>
          )}
        </div>
      </Portal>
      {currentUser?.id && (
        <EditCollaboratorsModal
          open={editCollModal}
          setOpen={setEditCollModal}
          currentUserId={currentUser.id}
          alreadyCollaborators={folder.collaborators}
          folderId={folder.id}
          ownerEmail={folder.owner.email}
        />
      )}

      {showChildOrgModal && (
        <ShareFolderToChildModal folder={folder} open={showChildOrgModal} setOpen={setShowChildOrgModal} folderId={folder.id} />
      )}
    </div>
  );
};

export default CollaborativeFolder;
